import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Field } from 'react-final-form';

import stripesFinalForm from '@folio/stripes/final-form';
import { Col, Button, Row, TextField } from '@folio/stripes/components';
import {
  TitleManager,
  withModules,
  withStripes,
  stripesShape,
  // kware start edit
  Pluggable,
  // kware end edit
} from '@folio/stripes/core';

import ErrorModal from '../ErrorModal';
import OverrideModal from '../OverrideModal';
import SelectItemModal from '../SelectItemModal';
// kware start edit - Backdated checkout date/time picker
import CheckoutDateTime from '../CheckoutDateTime';
// kware end edit

class ItemForm extends React.Component {
  static propTypes = {
    stripes: stripesShape.isRequired,
    shouldSubmitAutomatically: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    item: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    patron: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    onOverride: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    formRef: PropTypes.object.isRequired,
    checkoutError: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClearCheckoutErrors: PropTypes.func,
    modules: PropTypes.shape({
      app: PropTypes.arrayOf(PropTypes.object),
    }),
    onItemSelection: PropTypes.func.isRequired,
    onCloseSelectItemModal: PropTypes.func.isRequired,
    patronBlockOverriddenInfo: PropTypes.object.isRequired,
    // kware start edit - Backdated checkout props
    intl: PropTypes.object,
    // kware end edit
  };

  static defaultProps = {
    patron: {},
    item: {},
    items: null,
  };

  static getDerivedStateFromProps(props, { errors }) {
    return { errors: props.checkoutError || errors || [] };
  }

  constructor(props) {
    super(props);

    this.barcodeEl = React.createRef();
    this.readyPrefix = props.modules?.app?.find(
      (el) => el.module === '@folio/checkout',
    )?.readyPrefix;
    this.state = {
      overrideModalOpen: false,
      errors: [],
      // kware start edit - Backdated checkout state
      showCheckoutDatePickers: false,
      // kware end edit
    };
    // kware start edit
    this.selectInstance = this.selectInstance.bind(this);
    // kware end edit
  }

  componentDidMount() {
    if (this.props.patron) {
      this.focusInput();
    }
    this.props.formRef.current = this.props.form;
    // kware start edit
    // Set up global function for plugin to call
    window.selectItemFromPlugin = this.selectItemFromPlugin;
    // kware end edit
  }

  componentDidUpdate(prevProps) {
    const {
      shouldSubmitAutomatically,
      patron,
      form: { getState },
    } = this.props;

    const { submitSucceeded } = getState();

    if (
      document.activeElement === this.barcodeEl.current ||
      !patron ||
      !patron.id
    ) {
      return;
    }

    // Focus on the item barcode input after the patron is entered
    if (
      !shouldSubmitAutomatically &&
      (submitSucceeded ||
        !prevProps.patron || // ?
        prevProps.patron.id !== patron.id)
    ) {
      this.clearForm();
      this.focusInput();
    }
  }

  setError = (errors) => {
    this.setState({ errors });
  };

  openOverrideModal = () => {
    const { errors } = this.state;
    this.setState({
      overrideError: errors[0],
      overrideModalOpen: true,
    });
  };

  // kware start edit - Show backdated checkout date/time pickers
  showCheckoutDatePickers = () => {
    const { form, intl } = this.props;
    const now = new Date();
    const formattedDate = now.toISOString().substring(0, 10);
    const formattedTime = intl?.formatTime?.(now) || now.toTimeString().substring(0, 5);

    form.change('item.checkoutDate', formattedDate);
    form.change('item.checkoutTime', formattedTime);
    this.setState({ showCheckoutDatePickers: true });
  };

  resetCheckoutDatePickers = () => {
    const { form } = this.props;

    form.change('item.checkoutDate', '');
    form.change('item.checkoutTime', '');
    this.setState({ showCheckoutDatePickers: false });
  };
  // kware end edit

  closeOverrideModal = () => {
    this.setState({ overrideModalOpen: false });
  };

  focusInput() {
    this.barcodeEl.current.focus();
  }

  clearForm = () => {
    const { form, onClearCheckoutErrors } = this.props;

    this.setError([]);
    onClearCheckoutErrors();
    form.reset();
    // kware start edit - Reset date pickers to default state after checkout
    this.setState({ showCheckoutDatePickers: false });
    // kware end edit
  };

  handleItemSelection = (_, item) => {
    this.props.onItemSelection(_, item);
    this.focusInput();
  };

  handleCloseSelectItemModal = () => {
    this.props.onCloseSelectItemModal();
    this.clearForm();
    this.focusInput();
  };

  onSubmit = async (event) => {
    const { handleSubmit } = this.props;
    const errors = await handleSubmit(event);

    if (!isEmpty(errors)) {
      return errors;
    }

    return this.clearForm();
  };

  // kware start edit
  componentWillUnmount() {
    // Clean up global function
    delete window.selectItemFromPlugin;
  }

  selectItemFromPlugin = (item) => {
    const { form } = this.props;

    if (item?.barcode) {
      form.change('item.barcode', item.barcode);
      // Close the modal
      if (window.closePluginModal) {
        window.closePluginModal();
      }
    }
  };

  selectInstance = (selectedData) => {
    const { form } = this.props;

    // إذا تم اختيار مادة مباشرة من سيجمنت المواد
    if (selectedData?.barcode) {
      form.change('item.barcode', selectedData.barcode);
    }
    // إذا تم اختيار instance وله مادة واحدة فقط
    else if (selectedData?.items?.length === 1) {
      form.change('item.barcode', selectedData.items[0].barcode);
    }
  };
  // kware end edit

  render() {
    const {
      submitting,
      stripes,
      item,
      items,
      onOverride,
      patronBlockOverriddenInfo,
    } = this.props;

    const { errors, overrideError, overrideModalOpen } = this.state;
    const validationEnabled = false;

    return (
      <>
        <form id="item-form" data-testid="itemForm" onSubmit={this.onSubmit}>
          <Row id="section-item">
            <Col xs={4}>
              <FormattedMessage id="ui-checkout.scanOrEnterItemBarcode">
                {(placeholder) => (
                  <FormattedMessage id="ui-checkout.itemId">
                    {(ariaLabel) => (
                      <TitleManager
                        prefix={
                          this.readyPrefix && this.state.readyToScan
                            ? this.readyPrefix
                            : undefined
                        }
                      >
                        <Field
                          data-testid="itemBarcodeField"
                          fullWidth
                          name="item.barcode"
                          component={TextField}
                          aria-label={ariaLabel}
                          id="input-item-barcode"
                          placeholder={placeholder}
                          inputRef={this.barcodeEl}
                          validationEnabled={validationEnabled}
                          onFocus={
                            this.readyPrefix
                              ? () => this.setState({ readyToScan: true })
                              : undefined
                          }
                          onBlur={
                            this.readyPrefix
                              ? () => this.setState({ readyToScan: false })
                              : undefined
                          }
                        />
                      </TitleManager>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
              {/* kware start edit - Instance lookup below barcode in same column */}
              <Pluggable
                type="find-instance"
                searchLabel={
                  <FormattedMessage id="ui-checkout.instanceLookup" />
                }
                marginTop0
                searchButtonStyle="link"
                config={{
                  showItemsColumn: true,
                  disableRowClick: true,
                  modalSize: 'large',
                  availableSegments: [
                    { name: 'items' }
                  ]
                }}
                selectInstance={this.selectInstance}
              />
              {/* kware end edit */}
            </Col>
            <Col xs={2}>
              <Button
                data-testid="addItemButton"
                id="clickable-add-item"
                type="submit"
                buttonStyle="default"
                disabled={submitting}
              >
                <FormattedMessage id="ui-checkout.enter" />
              </Button>
            </Col>
            {/* kware start edit - Backdated checkout date/time picker in same row */}
            <CheckoutDateTime
              showPickers={this.state.showCheckoutDatePickers}
              onClick={this.showCheckoutDatePickers}
              onReset={this.resetCheckoutDatePickers}
            />
            {/* kware end edit */}
          </Row>
        </form>
        {!isEmpty(errors) && (
          <ErrorModal
            data-testid="errorModal"
            stripes={stripes}
            item={item || {}}
            errors={errors}
            open
            openOverrideModal={this.openOverrideModal}
            onClose={this.clearForm}
          />
        )}
        {overrideModalOpen && (
          <OverrideModal
            data-testid="overrideModal"
            item={item}
            overrideError={overrideError}
            stripes={stripes}
            onOverride={onOverride}
            closeOverrideModal={this.closeOverrideModal}
            patronBlockOverriddenInfo={patronBlockOverriddenInfo}
          />
        )}
        {items && (
          <SelectItemModal
            data-testid="selectItemModal"
            checkoutItems={items}
            onClose={this.handleCloseSelectItemModal}
            onSelectItem={this.handleItemSelection}
          />
        )}
      </>
    );
  }
}

export default stripesFinalForm({
  navigationCheck: true,
})(withStripes(withModules(ItemForm)));
